import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

// Tracks whether the most recent input was a keypress vs a pointer press, so
// `TooltipTrigger`'s `onFocus` below can tell "the user tabbed here" from
// "something else focused this." Module-scope, not component-scope: the
// pointerdown that triggers (for example) a dialog open — and its
// subsequent autofocus onto the first tabbable descendant — fires on a
// DIFFERENT element, before the eventual trigger even exists in the DOM, so
// a per-instance mount effect would attach its listener too late. This runs
// once, the first time any file imports this module — effectively at app
// boot, well before any dialog-opening click.
//
// Not CSS `:focus-visible`: tried first, and DISPROVEN by live-testing in
// real Chromium. A script-triggered `.focus()` call with no preceding
// pointer interaction (exactly what Radix Dialog's autofocus does) still
// matches `:focus-visible` there — the browser's algorithm treats "no
// established modality yet" as should-be-visible, it does not specially
// exempt script focus. Measured: open a dialog via mouse click, then
// `document.activeElement.matches(':focus-visible')` on the autofocused
// element returns `true`.
let lastInputWasKeyboard = false;
if (typeof document !== 'undefined') {
  document.addEventListener(
    'keydown',
    () => {
      lastInputWasKeyboard = true;
    },
    true,
  );
  document.addEventListener(
    'pointerdown',
    () => {
      lastInputWasKeyboard = false;
    },
    true,
  );
}

// Carries a per-`<Tooltip>` ref from `TooltipTrigger`'s `onFocus` down to
// this `Tooltip`'s own `onOpenChange`, so a focus that arrives via anything
// OTHER than a real keyboard Tab can veto the open before it ever commits.
// `null` outside a `Tooltip` (defensive — `TooltipTrigger` is only ever used
// inside one).
const TooltipSuppressContext = React.createContext<React.MutableRefObject<boolean> | null>(null);

/**
 * `Tooltip` — Radix's `Root`, wrapped so every tooltip in the app (both the
 * common `tooltip` prop on primitives like `CircleIconButton`, AND any
 * hand-rolled `<Tooltip><TooltipTrigger>…` usage) gets the same fix for one
 * recurring bug: a workspace `Inspector` is a raw `DialogPrimitive.Content`,
 * Radix Dialog autofocuses the first tabbable descendant on mount, and
 * Radix's own `TooltipTrigger` opens on ANY focus event — so that autofocus
 * alone opens a tooltip with no user interaction at all (first found on
 * `ConceptDesignBody`'s Back button, then `ImageBody`'s — see
 * `docs/planning/editor-unification-screenshots/P2-asset-reuse-note-desktop.png`).
 *
 * Always renders `TooltipPrimitive.Root` with an EXPLICIT `open` value
 * (ours if the caller didn't pass one, theirs if they did) rather than
 * leaving it `undefined`. This isn't cosmetic: Radix's own
 * `useControllableState` only calls `onOpenChange` SYNCHRONOUSLY, before any
 * state commit, when `open` is externally controlled — the default
 * (uncontrolled) path commits the internal `open` state immediately and
 * only notifies `onOpenChange` from a later `useEffect`, by which point a
 * veto is too late (the tooltip has already rendered open for a frame).
 * Forcing every consumer through our own controlled `open` is what makes
 * the veto in `handleOpenChange` below actually land before paint.
 *
 * A caller that already fully owns `open` (e.g. `slider.tsx`'s
 * drag-to-show-tooltip, which passes `open` with no `onOpenChange` at all)
 * is left alone: `isControlled` short-circuits our local state, and a
 * suppressed request simply doesn't call their `onOpenChange` — since they
 * don't have one, this is a no-op, identical to before.
 */
const Tooltip = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>) => {
  const isControlled = openProp !== undefined;
  const suppressNextOpenRef = React.useRef(false);
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const open = isControlled ? openProp : uncontrolledOpen;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (next && suppressNextOpenRef.current) {
        suppressNextOpenRef.current = false;
        return;
      }
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  return (
    <TooltipSuppressContext.Provider value={suppressNextOpenRef}>
      <TooltipPrimitive.Root open={open} onOpenChange={handleOpenChange} {...props} />
    </TooltipSuppressContext.Provider>
  );
};

/**
 * `TooltipTrigger` — Radix's `Trigger`, wrapped only to arm the suppression
 * ref above. `onFocus` runs BEFORE Radix's own opening handler (Radix's
 * `composeEventHandlers(props.onFocus, openHandler)` calls the caller's
 * handler first, then its own — verified by reading
 * `@radix-ui/react-tooltip`'s source, not assumed), so by the time Radix
 * asks `Tooltip` to open, the ref is already armed and `handleOpenChange`
 * above vetoes it in the same synchronous dispatch. A real Tab press leaves
 * `lastInputWasKeyboard` true, the ref is never armed, and the open request
 * passes through untouched — verified live by actually pressing Tab, not by
 * reasoning about it.
 */
const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onFocus, ...props }, ref) => {
  const suppressNextOpenRef = React.useContext(TooltipSuppressContext);
  const handleFocus = React.useCallback<React.FocusEventHandler<HTMLButtonElement>>(
    (event) => {
      onFocus?.(event);
      if (suppressNextOpenRef && !lastInputWasKeyboard) {
        suppressNextOpenRef.current = true;
      }
    },
    [onFocus, suppressNextOpenRef],
  );
  return <TooltipPrimitive.Trigger ref={ref} {...props} onFocus={handleFocus} />;
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, collisionPadding = 8, ...props }, ref) => (
  // Portal to document.body so the tooltip escapes the sidebar / sticky-header
  // stacking contexts. Without this, sidebar collapse-state tooltips render
  // inside SidebarInset (parent stacking context), so their z-50 loses to the
  // sticky page header's z-30 when the header creates its own stacking context
  // via `bg-background` + position:sticky. Result: the tooltip rectangle
  // appeared underneath the tab strip. Portalling fixes it structurally.
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      /*
       * 🔴 A TOOLTIP MUST NOT LEAVE THE VIEWPORT. Radix only keeps content on
       * screen if it is told how much room to leave; with no `collisionPadding`
       * it will happily anchor a wide bubble past the right edge. Measured at
       * 390px on the client desk: the "Quick decisions" hint ran off the screen
       * mid-sentence ("…Nothing here needs reading —") with no way to read the
       * rest, on the surface a client opens most often on a phone.
       *
       * The width cap is the other half — an unbounded tooltip on a narrow
       * screen becomes a full-bleed slab. 20rem is the comfortable reading
       * measure for one or two sentences; `90vw` keeps it inside any viewport
       * narrower than that. Overridable per call site via `className`.
       */
      collisionPadding={collisionPadding}
      className={cn(
        'z-popover overflow-hidden rounded-md border border-border/60 bg-popover/95 px-3 py-1.5 text-sm text-popover-foreground shadow-elevation-3 backdrop-blur-xl',
        'max-w-[min(90vw,20rem)]',
        // Smooth entrance/exit animations
        'animate-in fade-in-0 zoom-in-98 duration-150 ease-out',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-98 data-[state=closed]:duration-150',
        // Directional slide animations
        'data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1',
        'data-[state=closed]:data-[side=bottom]:slide-out-to-top-1 data-[state=closed]:data-[side=left]:slide-out-to-right-1 data-[state=closed]:data-[side=right]:slide-out-to-left-1 data-[state=closed]:data-[side=top]:slide-out-to-bottom-1',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = 'TooltipContent';

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
